import { isArray } from 'util'
import deepmerge from 'deepmerge'
import mapValues from 'lodash/mapValues'
import { NormalizedData } from 'src/graphql'

export const gatherNormalizedData = (value: any): Partial<NormalizedData> => {
  if (typeof value != 'object' || value == null) {
    return {}
  } else if (isArray(value)) {
    return deepmerge.all(value.map(gatherNormalizedData), deepmergeOptions)
  } else {
    return deepmerge.all(
      [
        isNormalizable(value)
          ? { [value.__typename]: { [value.id]: normalizeObject(value) } }
          : {},
        ...Object.values(value).map(gatherNormalizedData),
      ],
      deepmergeOptions,
    )
  }
}

const normalizeObject = <T extends object>(object: T) =>
  mapValues(object, normalizeValue)

const normalizeValue = (value: any): any => {
  if (typeof value != 'object' || value == null) {
    return value
  } else if (isArray(value)) {
    return value.map(normalizeValue)
  } else if (isNormalizable(value)) {
    return normalize(value)
  } else {
    return normalizeObject(value)
  }
}

export const deepmergeOptions: deepmerge.Options = {
  arrayMerge: (destination, source) => source,
}

const denormalizeObject = (object: any, data: NormalizedData): any =>
  mapValues(object, value => denormalizeValue(value, data))

const denormalizeValue = (value: any, data: NormalizedData): any => {
  if (typeof value != 'object' || value == null) {
    return value
  } else if (isArray(value)) {
    return value.map(x => denormalizeValue(x, data))
  } else if (isNormalized(value)) {
    return denormalize(data)(value)
  } else {
    return denormalizeObject(value, data)
  }
}

type Normalizable<K extends keyof NormalizedData> = {
  __typename: K
  id: string
}

const isNormalizable = (value: any): value is Normalizable<any> =>
  value && typeof value.__typename == 'string' && typeof value.id == 'string'

type KeyOf<T> = T extends Normalizable<infer K> ? K : never

export type Normalized<T extends Normalizable<any>> = {
  __typename: KeyOf<T>
  id: string
  isNormalized: true
}

const isNormalized = (value: any): value is Normalized<any> =>
  value &&
  typeof value.__typename == 'string' &&
  typeof value.id == 'string' &&
  value.isNormalized === true

export const normalize = <T extends Normalizable<any>>(
  value: T,
): Normalized<T> => ({
  __typename: value.__typename,
  id: value.id,
  isNormalized: true,
})

export const denormalize: <T extends Normalizable<any>>(
  data: NormalizedData,
) => (normalized: Normalized<T>) => T = data => normalized =>
  denormalizeObject(data[normalized.__typename][normalized.id], data)
